import { z } from 'zod';

/**
 * Funil de conversão — da instalação à primeira etiqueta impressa.
 *
 * Módulo próprio, e não dentro de `aggregations.ts`, porque o funil deixou
 * de ser uma lista de contagens: tem duas variantes de superfície, ordem
 * significativa e semântica de janela de conversão. Misturado com os demais
 * agregados, isso vira ruído.
 *
 * ## O que mudou em relação ao funil anterior (`DailyFunnelSchema`)
 *
 * O antigo tinha nove etapas e três delas (`SIGNUP_STARTED`, `OTP_SENT`,
 * `OTP_VERIFIED`) apontavam para eventos que nenhum código emitia — zero por
 * construção. `BILLING_ACTIVE` rodava a MESMA query que `TENANT_CREATED`,
 * logo não media pagamento. E cada etapa era uma contagem independente do
 * dia: não seguia a mesma pessoa, então a razão entre duas barras não era
 * taxa de conversão. O schema antigo continua existindo para os documentos
 * já gravados; nada novo deve usá-lo.
 *
 * ## Semântica de um documento
 *
 * Cada doc é o funil dos **últimos `windowDays` dias terminando naquele
 * dia**, por superfície, calculado pelo `FunnelsQuery` do PostHog — que
 * agrega por pessoa e respeita a ordem das etapas.
 *
 * Não é uma coorte diária, e a distinção importa: o `dateRange` do PostHog
 * restringe TODAS as etapas, então "quem entrou no dia D, medido por 14
 * dias" não é expressável — sairia uma janela móvel disfarçada de coorte.
 * Documentos de dias diferentes se sobrepõem e **não podem ser somados**.
 */

/**
 * As etapas, na ordem. Nem toda superfície usa todas — ver
 * `ANDROID_CONVERSION_STEPS` / `WEB_CONVERSION_STEPS`.
 */
export const ConversionStepSchema = z.enum([
  // Entrada, Android
  'APP_INSTALLED',
  'APP_OPENED',
  // Entrada, web
  'LANDING_VIEWED',
  'SIGNUP_VIEWED',
  // Comuns às duas
  'ACCOUNT_CREATED',
  'CONTACT_VERIFIED',
  'ONBOARDING_STARTED',
  'TENANT_CREATED',
  'PAYWALL_VIEWED',
  'CHECKOUT_STARTED',
  'PAID',
  'PRODUCT_CREATED',
  'LABEL_CREATED',
  'LABEL_PRINTED',
]);
export type ConversionStep = z.infer<typeof ConversionStepSchema>;

/**
 * Etapas depois da entrada — idênticas nas duas superfícies.
 *
 * Desde a #679 F4 não há mais cadastro, verificação, onboarding nem checkout
 * no app: o estabelecimento nasce na venda do consultor, e quem chega ao app já
 * tem conta. `ACCOUNT_CREATED`, `CONTACT_VERIFIED`, `ONBOARDING_STARTED`,
 * `TENANT_CREATED` e `CHECKOUT_STARTED` (e `SIGNUP_VIEWED`, da web) saíram das
 * listas; os valores continuam no enum porque os documentos diários
 * anteriores os têm. O paywall agora é a tela de espera do pagamento.
 */
const COMMON_STEPS = [
  'PAYWALL_VIEWED',
  'PAID',
  'PRODUCT_CREATED',
  'LABEL_CREATED',
  'LABEL_PRINTED',
] as const satisfies readonly ConversionStep[];

/**
 * Funil do Android: começa na instalação.
 *
 * `APP_INSTALLED` vem do evento `Application Installed` do SDK, que dispara
 * na **primeira abertura** — quem instala e nunca abre não existe no
 * PostHog. A conversão da etapa 1 para a 2 fica perto de 100% por
 * construção; instalação de verdade só no Play Console.
 */
export const ANDROID_CONVERSION_STEPS = [
  'APP_INSTALLED',
  'APP_OPENED',
  ...COMMON_STEPS,
] as const satisfies readonly ConversionStep[];

/**
 * Funil da web: começa na landing.
 *
 * `LABEL_PRINTED` continua no fim mesmo sem impressão pela web — mede quem
 * ENTROU pela web e imprimiu no celular, que é a jornada real. Impressora
 * Bluetooth é Android-only por design.
 *
 * Retenção NÃO entra aqui: uma contagem de tenants de um dia colada no fim
 * de um funil de pessoas de 30 dias renderiza como uma queda que não existe.
 * Ela vive em `/dashboard/cohorts`.
 */
export const WEB_CONVERSION_STEPS = [
  'LANDING_VIEWED',
  ...COMMON_STEPS,
] as const satisfies readonly ConversionStep[];

/** Superfície de entrada. */
export const ConversionSurfaceSchema = z.enum(['android', 'web', 'ios']);
export type ConversionSurface = z.infer<typeof ConversionSurfaceSchema>;

const NonNegInt = z.number().int().nonnegative();
const YyyymmddRegex = /^\d{8}$/;

/**
 * Contagem por etapa.
 *
 * `partialRecord`, não `record`: com chave de enum o `z.record` do Zod 4 é
 * EXAUSTIVO — exigiria as 15 etapas em cada superfície, e nenhuma das duas
 * tem todas (o Android não vê `LANDING_VIEWED`, a web não vê
 * `APP_INSTALLED`). Etapa ausente vale zero na leitura.
 */
const StepCounts = z.partialRecord(ConversionStepSchema, NonNegInt);

export const ConversionFunnelDaySchema = z.object({
  yyyymmdd: z.string().regex(YyyymmddRegex),
  schemaVersion: z.literal(2),
  /** Tamanho da janela que este doc cobre, terminando em `yyyymmdd`. */
  windowDays: NonNegInt,
  /** Teto de tempo entre a primeira e a última etapa, por pessoa. */
  conversionWindowDays: NonNegInt,
  bySurface: z.object({
    android: StepCounts,
    web: StepCounts,
  }),
  computedAt: NonNegInt,
  computedBy: z.enum(['aggregateFunnel', 'backfill']),
});
export type ConversionFunnelDay = z.infer<typeof ConversionFunnelDaySchema>;
